import { useInput, FieldTitle, InputHelperText, InputProps } from "react-admin";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";

/**
 * react-admin time input backed by MUI X <TimePicker> instead of the native
 * <input type="time">. The native input renders inconsistently on Safari
 * (no clock picker, empty/invalid value when typed via keyboard), whereas the
 * MUI picker is JS-rendered and behaves the same across browsers.
 *
 * Stores the value as a JS Date so downstream consumers can call getHours()/etc.
 */
const TimePickerInput = ({
  source,
  label,
  validate,
  defaultValue,
  disabled,
  fullWidth,
  resource,
  minutesStep = 5,
  ampm = true,
  ...rest
}: InputProps & {
  fullWidth?: boolean;
  disabled?: boolean;
  minutesStep?: number;
  ampm?: boolean;
}) => {
  const { field, fieldState, formState, isRequired } = useInput({
    source,
    validate,
    defaultValue,
    ...rest
  });

  const { error, invalid, isTouched } = fieldState;
  const { isSubmitted } = formState;
  const showError = (isTouched || isSubmitted) && invalid;

  const value = field.value ? dayjs(field.value) : null;

  return (
    <TimePicker
      label={
        <FieldTitle
          label={label}
          source={source}
          resource={resource}
          isRequired={isRequired}
        />
      }
      value={value}
      disabled={disabled}
      minutesStep={minutesStep}
      ampm={ampm}
      onChange={(date) =>
        field.onChange(
          date && dayjs(date).isValid() ? dayjs(date).toDate() : null
        )
      }
      slotProps={{
        textField: {
          fullWidth,
          margin: "dense",
          size: "small",
          variant: "filled",
          onBlur: field.onBlur,
          error: showError,
          helperText: showError ? (
            <InputHelperText touched={showError} error={error?.message} />
          ) : (
            <p className="MuiFormHelperText-root">&nbsp;</p>
          )
        }
      }}
    />
  );
};

export default TimePickerInput;
